import type { Episode, SeriesSeason, WatchProgressEntry } from "@hybrid-iptv/types";
import { isResumable } from "./progressStore.js";
import { episodeKey } from "./vodHooks.js";

/** What the series banner's first button plays, and what it says. */
export interface NextUp {
  episode: Episode;
  label: string;
}

/**
 * The episode the viewer is most likely after: the one last watched, if they
 * stopped part-way through it; the one after it, if they finished it; else the
 * first episode. Like Sparkle's "Play Season 1 Episode 1", which follows the
 * viewer through the series (owner, 2026-10-07).
 */
export function pickNextUp(
  seasons: SeriesSeason[],
  progressOf: (key: string) => WatchProgressEntry | undefined,
): NextUp | null {
  const episodes = [...seasons]
    .sort((a, b) => a.seasonNumber - b.seasonNumber)
    .flatMap((season) => [...season.episodes].sort((a, b) => a.episodeNumber - b.episodeNumber));
  const first = episodes[0];
  if (!first) return null;
  let lastIndex = -1;
  let last: WatchProgressEntry | undefined;
  episodes.forEach((episode, index) => {
    const progress = progressOf(episodeKey(episode));
    if (progress && (!last || progress.updatedAt > last.updatedAt)) {
      last = progress;
      lastIndex = index;
    }
  });
  const at = (episode: Episode) => `S${episode.seasonNumber} E${episode.episodeNumber}`;
  if (lastIndex < 0) return { episode: first, label: `▶ Play ${at(first)}` };
  const watched = episodes[lastIndex]!;
  if (isResumable(last)) return { episode: watched, label: `▶ Resume ${at(watched)}` };
  const next = episodes[lastIndex + 1];
  return next ? { episode: next, label: `▶ Next: ${at(next)}` } : { episode: first, label: `▶ Play ${at(first)}` };
}

/**
 * The episode's own name. Providers often repeat the series and the numbers in
 * it ("Atlantis - S01E04 - Twist of Fate"), which the row already shows.
 */
export function episodeName(episode: Episode): string {
  const trimmed = episode.title.replace(/^.*?\bS\d{1,3}\s*E\d{1,4}\b\s*[-–—:]?\s*/i, "").trim();
  return trimmed || episode.title.trim() || `Episode ${episode.episodeNumber}`;
}

/** "Sep 28, 2013" from "2013-09-28", read as a calendar date, not a moment. */
export function formatAirDate(airDate: string | undefined): string | null {
  if (!airDate) return null;
  const date = new Date(`${airDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** "45m" or "1h 05m". */
export function formatRuntime(minutes: number | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}
